import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { toggleModal, toggleSpinner } from 'apps/auth/actions/appAction';
import { addAccountData, updateCardsList } from 'apps/auth/actions/cardsAction';
import { addExtras } from 'apps/auth/actions/extraAction';
import { changeFormFieldValue, setNewFields, setNewForm } from 'apps/auth/actions/formAction';
import { updatePageMessages } from 'apps/auth/actions/messageAction';
import ProfileACHModal from 'apps/auth/components/cards/ProfileACHModal';
import { Button } from 'apps/auth/components/common/Button/Button';
import { FixedBottomPanel } from 'apps/auth/components/common/Layout/FixedBottomPanel/FixedBottomPanel';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { Row } from 'apps/auth/components/common/Layout/Row';
import AccountField from 'apps/auth/components/form/AccountField';
import InputAutoDetect from 'apps/auth/components/form/InputAutoDetect';
import Form from 'apps/auth/components/form-multi/Form';
import { clearLeadingSpace, getField, getSortFields } from 'apps/auth/components/form-multi/utils';
import { FROM_PRV_ID, TO_PRV_ID } from 'apps/auth/constants/fieldsName';
import { ADD_SOURCE_BANK_ACCOUNT, AML_LEVEL_FORM } from 'apps/auth/constants/formName';
import { AUTH_BANK_ACCOUNT_ADD, AUTH_BANK_ACCOUNT_ADD_ACTION } from 'apps/auth/constants/UrlConstants';
import { USA } from 'apps/auth/constants/WellKnownCountries';
import { apiFetchData } from 'apps/auth/helpers/api';
import { isObject } from 'apps/auth/helpers/utils';

const AddBankAccount = (props) => {
    const [isUseAchModal, setUseAchModal] = useState(false);

    useEffect(() => {
        if (!isObject(props.form)) {
            props.toggleSpinner(true);
            apiFetchData(AUTH_BANK_ACCOUNT_ADD)
                .then((json) => {
                    if (json.messages) {
                        props.updatePageMessages(json.messages);
                    }
                    if (json.extra) {
                        props.addExtras(json.extra);
                        setUseAchModal(
                            json.extra?.plaidAccountAction ||
                                json.extra?.canShowAmlLevelUpgrade ||
                                json.extra?.canShowOowFlow ||
                                json.extra?.needSecretQuestionsCheck,
                        );
                    }
                    if (json?.extra?.aml_level_form) {
                        props.setNewForm(AML_LEVEL_FORM, getProfileFields({ ...json.extra }));
                    }
                    props
                        .addAccountData(json.accountForm.account)
                        .then(() => props.setNewForm(ADD_SOURCE_BANK_ACCOUNT, getFields(json.accountForm)).then(() => props.toggleSpinner(false)));
                })
                .catch(() => props.toggleSpinner(false));
        }
        return () => {
            if (isObject(props.form) && isObject(props.form.fields)) {
                Object.values(props.form.fields).forEach((field) => props.changeFormFieldValue(ADD_SOURCE_BANK_ACCOUNT, '', field.name));
            }
        };
    }, []);

    const getFields = (accountForm) => {
        const fields = {};
        if (isObject(accountForm)) {
            const account = accountForm.account;
            fields[account.account.name] = account.account;
            fields[TO_PRV_ID] = getField(TO_PRV_ID, account.account.providerId, '', true, false, '', 'text', false);
            if (isObject(account.bik)) {
                fields[account.bik.name] = account.bik;
            }
            if (Array.isArray(accountForm.otherFields)) {
                accountForm.otherFields.forEach((otherField) => (fields[otherField.name] = otherField));
            }
        }
        return fields;
    };

    const getProfileFields = (extra) => {
        const fields = {};
        const amlForm = extra.aml_level_form;
        if (isObject(amlForm.fields.itin)) {
            fields[amlForm.fields.itin.name] = amlForm.fields.itin;
        }
        if (isObject(amlForm.fields.ssn)) {
            fields[amlForm.fields.ssn.name] = amlForm.fields.ssn;
        }
        if (isObject(amlForm.fields.issue)) {
            fields[amlForm.fields.issue.name] = amlForm.fields.issue;
        }
        if (extra.fromPrvId) {
            fields[FROM_PRV_ID] = getField(FROM_PRV_ID, extra.fromPrvId);
        }
        return fields;
    };

    const addField = (fieldName) => {
        if (fieldName === TO_PRV_ID || fieldName === props.account.account.name || (props.account.bik && fieldName === props.account.bik.name)) {
            return false;
        }
        return true;
    };

    const submitHandler = (fields, data) => {
        if (Object.keys(data.errors).length === 0) {
            props.updateCardsList(data.cards).then(() => props.toggleModal(false));
        }
    };

    const beforeSubmit = () => clearLeadingSpace(props.form.fields, ADD_SOURCE_BANK_ACCOUNT, props.changeFormFieldValue);

    if (isObject(props.form)) {
        return (
            <>
                <HeaderPanel
                    title={<>{props.cardsAddBankAccountTitle}</>}
                    subtitle={props.countryId === USA ? props.cardsAddBankCardUsaText : props.cardsAddBankCardText}
                />
                <Form
                    getCanonicalPath={true}
                    formName={ADD_SOURCE_BANK_ACCOUNT}
                    fields={props.form.fields}
                    beforeSubmit={beforeSubmit}
                    onSubmitHandler={submitHandler}
                    action={AUTH_BANK_ACCOUNT_ADD_ACTION}
                >
                    <Row indent="adaptive-24-32">
                        <AccountField formName={ADD_SOURCE_BANK_ACCOUNT} account={props.account} />
                    </Row>
                    {getSortFields(props.form.fields).map((field) => {
                        if (addField(field.name)) {
                            return (
                                <Row indent="adaptive-24-32" key={field.name}>
                                    <InputAutoDetect formName={ADD_SOURCE_BANK_ACCOUNT} field={field} />
                                </Row>
                            );
                        } else {
                            return false;
                        }
                    })}
                    <FixedBottomPanel isRightAligned>
                        <Button type="submit" raised={true} caption={props.cardsAddBankAccountButton} />
                    </FixedBottomPanel>
                </Form>
            </>
        );
    } else if (isUseAchModal) {
        return <ProfileACHModal extra={props.extra} fields={[]} />;
    } else {
        return false;
    }
};

const mapStateToProps = (state) => {
    return {
        form: state.form[ADD_SOURCE_BANK_ACCOUNT],
        account: state.cards.accountFields,
        cardsAddBankAccountButton: state.messages.pageMessages.cardsAddBankAccountButton,
        cardsAddBankAccountTitle: state.messages.pageMessages.cardsAddBankAccountTitle,
    };
};
export default connect(mapStateToProps, {
    setNewForm,
    changeFormFieldValue,
    updatePageMessages,
    toggleSpinner,
    toggleModal,
    addAccountData,
    updateCardsList,
    addExtras,
    setNewFields,
})(AddBankAccount);
